import { css } from '@emotion/react';
import styled from '@emotion/styled';
import React, { useCallback, useRef } from 'react';
import { SectionRecentlyReadDto, SectionRelayPersonalResponseDto } from '@diritto/common/relay/dtos/section-relay.dtos';
import {
  ContentListType,
  TrackerExtraInfoProps,
  TrackingEventType,
} from '@diritto/common/tracker/tracking-event.constant';
import { isPC } from 'constants/layout';
import { useAuthState } from 'contexts/AuthProvider';
import { SectionTitle } from 'app-components/home/sections/components/SectionTitle';
import {
  CoverImageRatio,
  CoverImageWidth,
  SectionRecentlyReadContentItem,
} from 'app-components/home/sections/SectionRecentlyReadContentItem';
import { useGetSectionsPersonalQuery } from 'queries/relay/useGetSectionsPersonalQuery';
import { hideScrollbar } from 'styles/common';
import { useTrackingState } from 'contexts/TrackingProvider';
import { useSlide } from 'ui-hooks/useSlide';
import { SectionSlideButton } from 'app-components/home/sections/components/SectionSlideButton';

type SectionProps = {
  section: SectionRecentlyReadDto;
};

const Height = 163;
const CoverImageHeight = CoverImageWidth * CoverImageRatio;
const VerticalGap = 8;
const HorizontalGap = 8;

export const SectionRecentlyReadContents = ({ section, trackerExtraInfo }: SectionProps & TrackerExtraInfoProps) => {
  const { user, isLoggedIn } = useAuthState();
  const { data, isFetched } = useGetSectionsPersonalQuery(!!user?.isAdult, section.position, isLoggedIn);

  if (user && !isFetched) {
    return (
      <div>
        <SectionTitle section={section} moreLink={section.moreLink} trackerExtraInfo={trackerExtraInfo} />
        <StyledScrollContainer rowCount={section.rowCount} />
      </div>
    );
  }

  if (!data || data.recentlyReadContents.length === 0) {
    return null;
  }

  return <SectionRecentlyReadContentList section={section} trackerExtraInfo={trackerExtraInfo} data={data} />;
};

const SectionRecentlyReadContentList = ({
  section,
  trackerExtraInfo,
  data,
}: SectionProps & TrackerExtraInfoProps & { data: SectionRelayPersonalResponseDto }) => {
  const { tracker } = useTrackingState();
  const slideRef = useRef<HTMLDivElement>(null);
  const { handlePrevSlide, handleNextSlide, handleSlide, showSlideButton } = useSlide(slideRef, HorizontalGap);

  const handleOnClickTracker = useCallback(
    (contentId: number, listItemIndex: number) => {
      tracker.track(TrackingEventType.SelectContentListItem, {
        listType: ContentListType.SectionRecentlyReadContent,
        contentId,
        listItemIndex,
        trackerExtraInfo,
      });
    },
    [tracker, trackerExtraInfo],
  );

  return (
    <div>
      <SectionTitle section={section} moreLink={section.moreLink} trackerExtraInfo={trackerExtraInfo} />
      <StyledWrapper>
        <SectionSlideButton
          showSlideButton={showSlideButton}
          handleNextSlide={handleNextSlide}
          handlePrevSlide={handlePrevSlide}
          // StyledContentMetaContainer의 height 만큼 버튼의 top을 위로 올려줌
          extraButtonTop={(-1 * (Height - CoverImageHeight)) / 2}
        />
        <StyledScrollContainer ref={slideRef} onScroll={handleSlide} rowCount={section.rowCount}>
          {data.recentlyReadContents.map((recentlyReadContent, index) => (
            <SectionRecentlyReadContentItem
              key={recentlyReadContent.id}
              onClick={() => handleOnClickTracker(recentlyReadContent.contentId, index)}
              recentlyReadContent={recentlyReadContent}
              contents={data.contents}
              episodes={data.episodes}
            />
          ))}
        </StyledScrollContainer>
      </StyledWrapper>
    </div>
  );
};

export default SectionRecentlyReadContents;

const StyledWrapper = styled.div`
  position: relative;
`;

const StyledScrollContainer = styled.div(
  ({ rowCount = 1 }: { rowCount?: number }) => css`
    position: relative;
    display: flex;
    flex-direction: column;
    flex-flow: column wrap;
    align-content: flex-start;
    padding: 0 16px;
    overflow-y: hidden;
    overflow-x: scroll;
    ${hideScrollbar};
    gap: ${VerticalGap}px ${HorizontalGap}px;
    height: ${Height * rowCount + VerticalGap * (rowCount - 1)}px;
    @media (${isPC}) {
      overflow-x: hidden;
      padding: 0;
      margin: 0 15px;
    }
  `,
);
